/**
 * Syndication Round Detail Page - Phase 7 Investor Syndication Layer.
 * Tabs: Commitments, Allocation, Wires, SPV, Settings.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Users,
  Banknote,
  Briefcase,
  Calculator,
  Settings as SettingsIcon,
  Plus,
  Trash2,
  Save,
  Crown,
  RefreshCw,
  PlayCircle,
  X,
  AlertTriangle,
  CheckCircle2,
  Layers3,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import syndicationService from '../services/syndicationService';
import {
  SyndicationRound,
  SyndicationCommitment,
  SyndicationStatus,
  CoInvestVisibility,
  CommitmentType,
  WireStatus,
  SpvProvider,
  SpvStatusResponse,
  AllocationOverride,
  AllocationPreviewEntry,
  InstrumentType,
} from '../types/syndication';
import AddCommitmentModal from '../components/AddCommitmentModal';
import WireStatusStepper from '../components/WireStatusStepper';
import { formatInvestorName } from '../components/CommitmentCard';
import { statusBadge, formatCurrency } from './SyndicationPage';

type TabId = 'commitments' | 'allocation' | 'wires' | 'spv' | 'settings';

const TABS: Array<{
  id: TabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'commitments', label: 'Commitments', icon: Users },
  { id: 'allocation', label: 'Allocation', icon: Calculator },
  { id: 'wires', label: 'Wires', icon: Banknote },
  { id: 'spv', label: 'SPV', icon: Briefcase },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const formatDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : '—';

const spvStatusBadge = (s?: string | null): string => {
  switch (s) {
    case 'formed':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'initiated':
    case 'pending':
      return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'failed':
      return 'bg-red-100 text-red-700 border-red-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

const SyndicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roundId = Number(id);

  const [round, setRound] = useState<SyndicationRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>('commitments');

  const [commitments, setCommitments] = useState<SyndicationCommitment[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  // Allocation overrides (commitment_id -> override amount as string)
  const [overrides, setOverrides] = useState<Record<number, string>>({});
  const [applyingAllocation, setApplyingAllocation] = useState(false);

  // Wire modal
  const [wireModalCommitment, setWireModalCommitment] =
    useState<SyndicationCommitment | null>(null);
  const [wireNextStatus, setWireNextStatus] = useState<WireStatus>('initiated');
  const [wireReference, setWireReference] = useState('');
  const [wireDate, setWireDate] = useState('');

  // SPV
  const [spvProvider, setSpvProvider] = useState<SpvProvider>('carta');
  const [spvStatus, setSpvStatus] = useState<SpvStatusResponse | null>(null);
  const [spvLoading, setSpvLoading] = useState(false);

  // Settings form
  const [formName, setFormName] = useState('');
  const [formTarget, setFormTarget] = useState<string>('');
  const [formMin, setFormMin] = useState<string>('');
  const [formMax, setFormMax] = useState<string>('');
  const [formInstrument, setFormInstrument] = useState<InstrumentType>('safe');
  const [formCarry, setFormCarry] = useState<string>('');
  const [formMgmtFee, setFormMgmtFee] = useState<string>('');
  const [formAdminFee, setFormAdminFee] = useState<string>('');
  const [formVisibility, setFormVisibility] =
    useState<CoInvestVisibility>('transparent');
  const [formStatus, setFormStatus] = useState<SyndicationStatus>('draft');
  const [formCloseDate, setFormCloseDate] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [closingRound, setClosingRound] = useState(false);

  const applyRoundData = (r: SyndicationRound) => {
    setRound(r);
    if (r.commitments) setCommitments(r.commitments);
    if (r.spv_provider) setSpvProvider(r.spv_provider);
    if (r.spv_formation_status || r.spv_external_id || r.spv_provider) {
      setSpvStatus({
        provider: r.spv_provider || null,
        external_id: r.spv_external_id || null,
        formation_status: r.spv_formation_status || 'not_started',
      });
    }
    setFormName(r.round_name);
    setFormTarget(String(r.target_amount ?? ''));
    setFormMin(r.minimum_commitment != null ? String(r.minimum_commitment) : '');
    setFormMax(r.maximum_commitment != null ? String(r.maximum_commitment) : '');
    setFormInstrument((r.instrument_type as InstrumentType) || 'safe');
    setFormCarry(
      r.spv_carry_percent != null ? String(r.spv_carry_percent) : ''
    );
    setFormMgmtFee(
      r.spv_management_fee_percent != null
        ? String(r.spv_management_fee_percent)
        : ''
    );
    setFormAdminFee(r.spv_admin_fee != null ? String(r.spv_admin_fee) : '');
    setFormVisibility(r.co_invest_visibility || 'transparent');
    setFormStatus(r.status);
    setFormCloseDate(r.close_date ? r.close_date.split('T')[0] : '');
  };

  const loadRound = async () => {
    if (!roundId) {
      toast.error('Invalid round id');
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await syndicationService.fetchRound(roundId);
    if (res.error) {
      toast.error(res.error);
    } else if (res.data) {
      applyRoundData(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId]);

  // Anonymized indexing for co_invest_visibility='anonymized'
  const anonymousIndexMap = useMemo(() => {
    const sorted = [...commitments].sort((a, b) => a.id - b.id);
    const map: Record<number, number> = {};
    sorted.forEach((c, idx) => {
      map[c.id] = idx + 1;
    });
    return map;
  }, [commitments]);

  const visibility: CoInvestVisibility =
    round?.co_invest_visibility || 'transparent';

  // -------- Commitments handlers --------

  const handleAddCommitment = async (payload: {
    investor_id: number;
    commitment_type: CommitmentType;
    amount: number;
    priority_rank?: number | null;
    lead_investor?: boolean;
    notes?: string | null;
  }) => {
    const res = await syndicationService.createCommitment(roundId, payload);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Commitment added');
    setAddOpen(false);
    await loadRound();
  };

  const handleUpdateCommitment = async (
    c: SyndicationCommitment,
    patch: Partial<SyndicationCommitment>
  ) => {
    const res = await syndicationService.updateCommitment(c.id, patch as any);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setCommitments((list) =>
        list.map((x) => (x.id === c.id ? res.data! : x))
      );
    }
  };

  const handleDeleteCommitment = async (c: SyndicationCommitment) => {
    if (!window.confirm('Delete this commitment?')) return;
    const res = await syndicationService.deleteCommitment(c.id);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Commitment deleted');
    setCommitments((list) => list.filter((x) => x.id !== c.id));
  };

  // -------- Wire handlers --------

  const openWireModal = (c: SyndicationCommitment) => {
    const progression: WireStatus[] = [
      'pending',
      'initiated',
      'received',
      'confirmed',
    ];
    const idx = progression.indexOf(c.wire_status);
    if (idx === -1 || idx >= progression.length - 1) {
      toast('Wire already at final stage', { icon: 'ℹ️' });
      return;
    }
    setWireModalCommitment(c);
    setWireNextStatus(progression[idx + 1]);
    setWireReference(c.wire_reference || '');
    setWireDate(
      c.wire_date ? c.wire_date.split('T')[0] : new Date().toISOString().split('T')[0]
    );
  };

  const submitWireUpdate = async () => {
    if (!wireModalCommitment) return;
    const res = await syndicationService.updateWire(wireModalCommitment.id, {
      status: wireNextStatus,
      wire_reference: wireReference.trim() || null,
      wire_date: wireDate || null,
    });
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      setCommitments((list) =>
        list.map((x) => (x.id === res.data!.id ? res.data! : x))
      );
    }
    toast.success(`Wire marked ${wireNextStatus}`);
    setWireModalCommitment(null);
  };

  // -------- Allocation handlers --------

  const allocationPreview: AllocationPreviewEntry[] = useMemo(() => {
    if (round?.allocation_preview && round.allocation_preview.length > 0) {
      return round.allocation_preview;
    }
    // Fallback: compute a naive priority-ordered preview on the client
    const target = round?.target_amount ?? 0;
    const sorted = [...commitments].sort((a, b) => {
      const pa = a.priority_rank ?? 9999;
      const pb = b.priority_rank ?? 9999;
      if (pa !== pb) return pa - pb;
      return a.created_at.localeCompare(b.created_at);
    });
    let remaining = target;
    return sorted.map((c) => {
      const alloc = Math.max(0, Math.min(c.amount, remaining));
      remaining -= alloc;
      return {
        commitment_id: c.id,
        investor_id: c.investor_id,
        requested_amount: c.amount,
        priority_rank: c.priority_rank ?? null,
        proposed_allocation: alloc,
      };
    });
  }, [round, commitments]);

  const totalAllocated = useMemo(() => {
    return allocationPreview.reduce((sum, entry) => {
      const override = overrides[entry.commitment_id];
      const val =
        override != null && override !== ''
          ? Number(override)
          : entry.proposed_allocation;
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }, [allocationPreview, overrides]);

  const applyAllocation = async () => {
    const overridesPayload: AllocationOverride[] = Object.entries(overrides)
      .filter(([, v]) => v !== '' && v != null)
      .map(([cid, v]) => ({
        commitment_id: Number(cid),
        allocated_amount: Number(v),
      }));
    setApplyingAllocation(true);
    const res = await syndicationService.applyAllocation(roundId, {
      overrides: overridesPayload.length > 0 ? overridesPayload : undefined,
    });
    setApplyingAllocation(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Allocation applied');
    setOverrides({});
    if (res.data) applyRoundData(res.data);
  };

  // -------- SPV handlers --------

  const handleInitiateSpv = async () => {
    setSpvLoading(true);
    const res = await syndicationService.initiateSpv(roundId, {
      provider: spvProvider,
    });
    setSpvLoading(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) setSpvStatus(res.data);
    toast.success('SPV formation initiated (stubbed)');
  };

  const handleRefreshSpv = async () => {
    setSpvLoading(true);
    const res = await syndicationService.fetchSpvStatus(roundId);
    setSpvLoading(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) setSpvStatus(res.data);
  };

  // -------- Settings handlers --------

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    const res = await syndicationService.updateRound(roundId, {
      round_name: formName,
      target_amount: Number(formTarget),
      minimum_commitment: formMin === '' ? null : Number(formMin),
      maximum_commitment: formMax === '' ? null : Number(formMax),
      instrument_type: formInstrument || null,
      spv_carry_percent: formCarry === '' ? null : Number(formCarry),
      spv_management_fee_percent:
        formMgmtFee === '' ? null : Number(formMgmtFee),
      spv_admin_fee: formAdminFee === '' ? null : Number(formAdminFee),
      co_invest_visibility: formVisibility,
      status: formStatus,
      close_date: formCloseDate || null,
    });
    setSavingSettings(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) {
      applyRoundData(res.data);
      toast.success('Settings saved');
    }
  };

  const handleCloseRound = async () => {
    if (
      !window.confirm(
        'Close this round? This will finalize allocations and prevent new commitments.'
      )
    )
      return;
    setClosingRound(true);
    const res = await syndicationService.closeRound(roundId);
    setClosingRound(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    if (res.data) applyRoundData(res.data);
    toast.success('Round closed');
  };

  const handleDeleteRound = async () => {
    if (!window.confirm('Delete this round? This cannot be undone.')) return;
    const res = await syndicationService.deleteRound(roundId);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success('Round deleted');
    navigate('/syndication');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading round...
        </div>
      </DashboardLayout>
    );
  }

  if (!round) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <p className="mb-4">Round not found.</p>
          <Link to="/syndication" className="text-blue-600 hover:underline">
            Back to syndication
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const linkedName =
    round.project?.project_name ||
    round.startup?.name ||
    (round.project_id
      ? `Project #${round.project_id}`
      : round.startup_id
      ? `Startup #${round.startup_id}`
      : '—');

  const target = round.target_amount || 1;
  const committedTotal =
    round.totals?.committed_amount ??
    round.committed_amount ??
    commitments.reduce((s, c) => s + (c.amount || 0), 0);
  const allocatedTotal =
    round.totals?.allocated_amount ??
    round.allocated_amount ??
    commitments.reduce((s, c) => s + (c.allocated_amount || 0), 0);
  const confirmedWires = commitments
    .filter((c) => c.wire_status === 'confirmed')
    .reduce((s, c) => s + (c.allocated_amount || c.amount || 0), 0);

  const committedPct = Math.min(100, (committedTotal / target) * 100);
  const allocatedPct = Math.min(100, (allocatedTotal / target) * 100);
  const wiredPct = Math.min(100, (confirmedWires / target) * 100);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/syndication"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to syndication
        </Link>

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <Layers3 className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">
                  {round.round_name}
                </h1>
                <div className="text-sm text-gray-600 mt-0.5">
                  <span className="text-gray-500">Linked:</span> {linkedName}
                  {round.pipeline_id && (
                    <>
                      {' · '}
                      <Link
                        to={`/deal-pipeline/${round.pipeline_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        Pipeline #{round.pipeline_id}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`px-2.5 py-1 rounded border text-xs font-medium capitalize ${statusBadge(
                  round.status
                )}`}
              >
                {round.status.replace('_', ' ')}
              </span>
              {(round.spv_formation_status || round.spv_provider) && (
                <span
                  className={`px-2.5 py-1 rounded border text-xs font-medium capitalize ${spvStatusBadge(
                    round.spv_formation_status
                  )}`}
                >
                  SPV: {round.spv_formation_status || 'not started'}
                </span>
              )}
            </div>
          </div>

          {/* Progress bars */}
          <div className="mt-5 space-y-3">
            <ProgressBar
              label="Committed"
              value={committedTotal}
              target={round.target_amount}
              pct={committedPct}
              color="bg-blue-500"
            />
            <ProgressBar
              label="Allocated"
              value={allocatedTotal}
              target={round.target_amount}
              pct={allocatedPct}
              color="bg-purple-500"
            />
            <ProgressBar
              label="Wires confirmed"
              value={confirmedWires}
              target={round.target_amount}
              pct={wiredPct}
              color="bg-green-500"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-2 overflow-x-auto">
            {TABS.map(({ id: tid, label, icon: Icon }) => (
              <button
                key={tid}
                onClick={() => setTab(tid)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  tab === tid
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* ---------- Commitments Tab ---------- */}
        {tab === 'commitments' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {commitments.length} commitment
                {commitments.length === 1 ? '' : 's'}
                {' · '}Visibility:{' '}
                <span className="font-medium capitalize">
                  {visibility.replace('_', ' ')}
                </span>
              </div>
              <button
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Commitment
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {commitments.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No commitments yet.
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Investor
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">
                        Amount
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Type
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Priority
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Lead
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Notes
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {commitments.map((c) => {
                      const displayName = formatInvestorName(
                        c,
                        visibility,
                        anonymousIndexMap[c.id]
                      );
                      const showFirm =
                        visibility === 'transparent' ||
                        (visibility === 'lead_only' && c.lead_investor);
                      return (
                        <tr key={c.id}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-900 inline-flex items-center gap-1">
                              {displayName}
                              {c.lead_investor && (
                                <Crown className="w-3.5 h-3.5 text-amber-600" />
                              )}
                            </div>
                            {showFirm && c.investor?.firm_name && (
                              <div className="text-xs text-gray-500">
                                {c.investor.firm_name}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {formatCurrency(c.amount)}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() =>
                                handleUpdateCommitment(c, {
                                  commitment_type:
                                    c.commitment_type === 'soft' ? 'hard' : 'soft',
                                })
                              }
                              className={`px-2 py-0.5 rounded text-xs font-medium capitalize border ${
                                c.commitment_type === 'hard'
                                  ? 'bg-green-100 text-green-700 border-green-300'
                                  : 'bg-amber-100 text-amber-700 border-amber-300'
                              }`}
                              title="Click to toggle soft/hard"
                            >
                              {c.commitment_type}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              defaultValue={c.priority_rank ?? ''}
                              onBlur={(e) => {
                                const v = e.target.value;
                                const newRank = v === '' ? null : Number(v);
                                if (newRank !== (c.priority_rank ?? null)) {
                                  handleUpdateCommitment(c, {
                                    priority_rank: newRank,
                                  });
                                }
                              }}
                              className="w-16 px-2 py-1 border border-gray-200 rounded text-xs"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={!!c.lead_investor}
                              onChange={(e) =>
                                handleUpdateCommitment(c, {
                                  lead_investor: e.target.checked,
                                })
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate">
                            {c.notes || '—'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => handleDeleteCommitment(c)}
                              className="inline-flex items-center px-2 py-1 border border-red-300 text-red-700 rounded text-xs hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ---------- Allocation Tab ---------- */}
        {tab === 'allocation' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Allocation preview
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Proposed allocations are computed by priority rank; you may
                    override specific commitments before applying.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500">
                    Total (proposed + overrides)
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      totalAllocated > round.target_amount
                        ? 'text-red-600'
                        : 'text-gray-900'
                    }`}
                  >
                    {formatCurrency(totalAllocated)} /{' '}
                    {formatCurrency(round.target_amount)}
                  </div>
                </div>
              </div>
              {totalAllocated > round.target_amount && (
                <div className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm inline-flex items-start">
                  <AlertTriangle className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  Total allocation exceeds the round target by{' '}
                  {formatCurrency(totalAllocated - round.target_amount)}.
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {allocationPreview.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No commitments to allocate.
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Investor
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">
                        Requested
                      </th>
                      <th className="text-left px-3 py-2 font-medium text-gray-700">
                        Priority
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">
                        Proposed
                      </th>
                      <th className="text-right px-3 py-2 font-medium text-gray-700">
                        Override
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {allocationPreview.map((entry) => {
                      const commitment = commitments.find(
                        (c) => c.id === entry.commitment_id
                      );
                      const name = commitment
                        ? formatInvestorName(
                            commitment,
                            visibility,
                            anonymousIndexMap[commitment.id]
                          )
                        : `Investor #${entry.investor_id}`;
                      return (
                        <tr key={entry.commitment_id}>
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {name}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(entry.requested_amount)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {entry.priority_rank ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {formatCurrency(entry.proposed_allocation)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <input
                              type="number"
                              placeholder="—"
                              value={overrides[entry.commitment_id] ?? ''}
                              onChange={(e) =>
                                setOverrides({
                                  ...overrides,
                                  [entry.commitment_id]: e.target.value,
                                })
                              }
                              className="w-28 px-2 py-1 border border-gray-200 rounded text-xs text-right"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={applyAllocation}
                disabled={
                  applyingAllocation || allocationPreview.length === 0
                }
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {applyingAllocation ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Apply allocation
              </button>
            </div>
          </div>
        )}

        {/* ---------- Wires Tab ---------- */}
        {tab === 'wires' && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {commitments.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                No commitments to track.
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">
                      Investor
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-700">
                      Amount
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">
                      Wire status
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">
                      Reference
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">
                      Wire date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {commitments.map((c) => {
                    const name = formatInvestorName(
                      c,
                      visibility,
                      anonymousIndexMap[c.id]
                    );
                    return (
                      <tr key={c.id}>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {name}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(c.allocated_amount || c.amount)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => openWireModal(c)}
                            className="text-left"
                            title="Click to advance wire status"
                          >
                            <WireStatusStepper status={c.wire_status} />
                          </button>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {c.wire_reference || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {formatDate(c.wire_date)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ---------- SPV Tab ---------- */}
        {tab === 'spv' && (
          <div className="space-y-4 max-w-2xl">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 inline-flex items-start">
              <AlertTriangle className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
              SPV formation is scaffolding only. The backend stubs provider
              responses; real Carta / Stripe Atlas integration is out of scope.
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Current SPV status
                </h3>
                <button
                  onClick={handleRefreshSpv}
                  disabled={spvLoading}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs hover:bg-gray-50 disabled:opacity-50"
                >
                  {spvLoading ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  )}
                  Refresh status
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Provider
                  </div>
                  <div className="text-gray-900 capitalize mt-1">
                    {spvStatus?.provider
                      ? spvStatus.provider.replace('_', ' ')
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    External ID
                  </div>
                  <div className="text-gray-900 mt-1 font-mono text-xs break-all">
                    {spvStatus?.external_id || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">
                    Formation status
                  </div>
                  <span
                    className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium border capitalize ${spvStatusBadge(
                      spvStatus?.formation_status
                    )}`}
                  >
                    {(spvStatus?.formation_status || 'not_started').replace(
                      '_',
                      ' '
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">
                Initiate SPV formation
              </h3>
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Provider
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {(
                    [
                      { value: 'carta', label: 'Carta' },
                      { value: 'stripe_atlas', label: 'Stripe Atlas' },
                      { value: 'other', label: 'Other' },
                    ] as Array<{ value: SpvProvider; label: string }>
                  ).map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${
                        spvProvider === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="spv_provider"
                        checked={spvProvider === opt.value}
                        onChange={() => setSpvProvider(opt.value)}
                      />
                      <span className="text-sm font-medium text-gray-900">
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <button
                onClick={handleInitiateSpv}
                disabled={spvLoading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {spvLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="w-4 h-4 mr-2" />
                )}
                Initiate SPV
              </button>
            </div>
          </div>
        )}

        {/* ---------- Settings Tab ---------- */}
        {tab === 'settings' && (
          <div className="space-y-4 max-w-2xl">
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Round name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Target
                  </label>
                  <input
                    type="number"
                    value={formTarget}
                    onChange={(e) => setFormTarget(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Min commitment
                  </label>
                  <input
                    type="number"
                    value={formMin}
                    onChange={(e) => setFormMin(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Max commitment
                  </label>
                  <input
                    type="number"
                    value={formMax}
                    onChange={(e) => setFormMax(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Instrument
                  </label>
                  <select
                    value={formInstrument}
                    onChange={(e) => setFormInstrument(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="safe">SAFE</option>
                    <option value="note">Convertible Note</option>
                    <option value="equity">Priced Equity</option>
                    <option value="warrant">Warrant</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Status
                  </label>
                  <select
                    value={formStatus}
                    onChange={(e) =>
                      setFormStatus(e.target.value as SyndicationStatus)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="soft_closing">Soft closing</option>
                    <option value="hard_closing">Hard closing</option>
                    <option value="closed">Closed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Carry %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formCarry}
                    onChange={(e) => setFormCarry(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Mgmt fee %
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formMgmtFee}
                    onChange={(e) => setFormMgmtFee(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    Admin fee ($)
                  </label>
                  <input
                    type="number"
                    value={formAdminFee}
                    onChange={(e) => setFormAdminFee(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Co-invest visibility
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {(
                    [
                      { value: 'transparent', label: 'Transparent' },
                      { value: 'anonymized', label: 'Anonymized' },
                      { value: 'lead_only', label: 'Lead only' },
                    ] as Array<{ value: CoInvestVisibility; label: string }>
                  ).map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${
                        formVisibility === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="form_visibility"
                        checked={formVisibility === opt.value}
                        onChange={() => setFormVisibility(opt.value)}
                      />
                      <span className="text-sm text-gray-900">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Close date
                </label>
                <input
                  type="date"
                  value={formCloseDate}
                  onChange={(e) => setFormCloseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-between pt-2">
                <div className="flex gap-2">
                  <button
                    onClick={handleCloseRound}
                    disabled={closingRound || round.status === 'closed'}
                    className="inline-flex items-center px-3 py-2 border border-amber-300 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-50 disabled:opacity-50"
                  >
                    {closingRound ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Close Round
                  </button>
                  <button
                    onClick={handleDeleteRound}
                    className="inline-flex items-center px-3 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                </div>
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingSettings ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save settings
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Commitment Modal */}
      <AddCommitmentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onConfirm={handleAddCommitment}
        minimumCommitment={round.minimum_commitment}
        maximumCommitment={round.maximum_commitment}
        defaultPriorityRank={commitments.length + 1}
      />

      {/* Wire update modal */}
      {wireModalCommitment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setWireModalCommitment(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Advance wire status
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  Mark as <span className="font-medium capitalize">{wireNextStatus}</span>
                </p>
              </div>
              <button
                onClick={() => setWireModalCommitment(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Wire reference
              </label>
              <input
                type="text"
                value={wireReference}
                onChange={(e) => setWireReference(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. ABC123XYZ"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Wire date
              </label>
              <input
                type="date"
                value={wireDate}
                onChange={(e) => setWireDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setWireModalCommitment(null)}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={submitWireUpdate}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

interface ProgressBarProps {
  label: string;
  value: number;
  target: number;
  pct: number;
  color: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  label,
  value,
  target,
  pct,
  color,
}) => (
  <div>
    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
      <span className="font-medium text-gray-700">{label}</span>
      <span>
        {formatCurrency(value)} / {formatCurrency(target)} ({pct.toFixed(0)}%)
      </span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
      <div className={`h-2.5 ${color}`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

export default SyndicationDetailPage;
